import {
  BrainCircuit,
  ChartPie,
  GroupIcon,
  Megaphone,
  MessageCircleReply,
  MessageSquareText,
  SettingsIcon,
  Users,
  WorkflowIcon,
  ZapIcon,
} from 'lucide-react'
import { useParams } from 'react-router'

import { BubbleIcon } from '@/components/icons/BubbleIcon'
import { SidebarItem, SidebarSubMenuItem } from '@/components/sidebar/types'
import { useUser } from '@/contexts/auth/user'
import useTranslator from '@/hooks/translation'
import { useQueryParams } from '@/hooks/use-query-params'
import { useFeatureFlags } from '@/stores/use-feature-flags'
import { toQueryString } from '@/utils/to-query-string'

import { useDashboardEditDialog } from './components/dashboards/dialogs/edit/hooks/use-dashboard-edit-dialog'
import { DashboardActionsPopover } from './components/popovers/dashboard-actions'
import type { UseDashboardsResult } from './hooks/useDashboards'

export const useMenuItems = ({
  state: dashboardsState,
  dashboards,
  reloadDashboards,
}: UseDashboardsResult): SidebarItem[] => {
  const { user } = useUser()
  const { featureFlags } = useFeatureFlags()

  const isUserRootOrAdmin = user?.is_root || user?.is_admin

  const params = useParams()
  const { getQueryParams } = useQueryParams()
  const { getTranslation } = useTranslator()

  const { onOpenChange } = useDashboardEditDialog()

  const botId = params.botId!

  const handleRetrySubItems = () => {
    reloadDashboards(botId)
  }

  const { companyId, projectId } = getQueryParams()

  const queryString = toQueryString({
    companyId,
    projectId,
  })

  return [
    {
      index: 12,
      icon: <BrainCircuit />,
      title: 'Finn',
      path: `/bots/${botId}/finn?${queryString}`,
      disabled: false,
      hidden: !featureFlags.finn,
    },
    {
      index: 0,
      icon: <WorkflowIcon />,
      title: getTranslation('bots.sidebar.builder'),
      path: `/bots/${botId}/builder?${queryString}`,
      disabled: false,
      hidden: false,
    },
    {
      index: 2,
      icon: <BubbleIcon className="text-current" />,
      title: getTranslation('bots.sidebar.bubble.bubble'),
      path: `/bots/${botId}/agentik?${queryString}`,
      disabled: false,
      hidden: !featureFlags.bubble,
      subItems: [
        {
          index: 0,
          title: getTranslation('bots.sidebar.bubble.agents'),
          path: `/bots/${botId}/agentik/agents/list?${queryString}`,
          disabled: false,
          hidden: false,
        },
        {
          index: 1,
          title: getTranslation('bots.sidebar.bubble.tasks'),
          path: `/bots/${botId}/agentik/tools/list?${queryString}`,
          disabled: false,
          hidden: false,
        },
      ],
    },
    {
      index: 3,
      icon: <ChartPie />,
      title: getTranslation('bots.sidebar.dashboard'),
      path: `/bots/${botId}/dashboards?${queryString}`,
      disabled: false,
      hidden: false,
      subItems: dashboards.map(
        (dashboard, index): SidebarSubMenuItem => ({
          index,
          title: dashboard.name,
          path: `/bots/${botId}/dashboards/${dashboard.dashboard_id}?${queryString}`,
          disabled: false,
          hidden: false,
          actionsPopover: isUserRootOrAdmin ? (
            <DashboardActionsPopover dashboard={dashboard} />
          ) : null,
        }),
      ),
      subItemsState: dashboardsState,
      onRetrySubItems: handleRetrySubItems,
      onAddSubItem: isUserRootOrAdmin
        ? () => {
            onOpenChange(true)
          }
        : undefined,
    },
    {
      index: 4,
      icon: <Users />,
      title: getTranslation('bots.sidebar.members'),
      path: `/bots/${botId}/members?${queryString}`,
      disabled: false,
      hidden: false,
    },
    {
      index: 5,
      icon: <MessageSquareText />,
      title: getTranslation('bots.sidebar.conversationHistory'),
      path: `/bots/${botId}/conversation-history/list?${queryString}`,
      disabled: false,
      hidden: !featureFlags.conversationHistory,
    },
    {
      index: 6,
      icon: <GroupIcon />,
      title: getTranslation('bots.sidebar.knowledgeBase'),
      path: `/bots/${botId}/knowledge-base/content-groups/list?${queryString}`,
      disabled: false,
      hidden: false,
    },
    {
      index: 7,
      icon: <MessageCircleReply />,
      title: getTranslation('bots.sidebar.followUp'),
      path: `/bots/${botId}/follow-up/bot-rules/list?${queryString}`,
      disabled: false,
      hidden: !featureFlags.followUp,
    },
    {
      index: 8,
      icon: <Megaphone />,
      title: getTranslation('bots.sidebar.campaigns.campaignModule'),
      path: `/bots/${botId}/campaign-module?${queryString}`,
      disabled: false,
      hidden: false,
      subItems: [
        {
          index: 0,
          title: getTranslation('bots.sidebar.campaigns.templates'),
          path: `/bots/${botId}/campaign-module/templates/list?${queryString}`,
          disabled: false,
          hidden: false,
        },
        {
          index: 2,
          title: getTranslation('bots.sidebar.campaigns.campaigns'),
          path: `/bots/${botId}/campaign-module/campaigns/list?${queryString}`,
          disabled: false,
          hidden: false,
        },
        {
          index: 1,
          title: getTranslation('bots.sidebar.campaigns.actionings'),
          path: `/bots/${botId}/campaign-module/actionings/list?${queryString}`,
          disabled: false,
          hidden: false,
        },
      ],
    },
    {
      index: 11,
      icon: <Megaphone />,
      // TODO: Remove (New) once the Campaign module is totally migrated
      title: `${getTranslation('bots.sidebar.campaign.title')} (New)`,
      path: `/bots/${botId}/campaign?${queryString}`,
      disabled: false,
      hidden: !featureFlags.campaign,
      subItems: [
        {
          index: 0,
          title: getTranslation('bots.sidebar.campaign.templates'),
          path: `/bots/${botId}/campaign/templates/list?${queryString}`,
          disabled: false,
          hidden: false,
        },
        {
          index: 1,
          title: getTranslation('bots.sidebar.campaign.dispatches'),
          path: `/bots/${botId}/campaign/dispatches/list?${queryString}`,
          disabled: false,
          hidden: false,
        },
      ],
    },
    {
      index: 9,
      icon: <ZapIcon />,
      title: getTranslation('bots.sidebar.n8n'),
      path: `/bots/${botId}/n8n?${queryString}`,
      disabled: false,
      hidden: !featureFlags.n8n,
    },
    {
      index: 10,
      icon: <SettingsIcon />,
      title: getTranslation('bots.sidebar.settings.title'),
      path: `/bots/${botId}/settings/integrations?${queryString}`,
      disabled: false,
      // TODO: Activate once the members is moved to here or integrations is ready
      hidden: !featureFlags.integrations || !isUserRootOrAdmin,
      subItems: [
        {
          index: 0,
          title: getTranslation('bots.sidebar.settings.integrations'),
          path: `/bots/${botId}/settings/integrations/list?${queryString}`,
          disabled: false,
          hidden: !featureFlags.integrations || !isUserRootOrAdmin,
        },
        {
          index: 1,
          title: getTranslation('bots.sidebar.settings.migrations'),
          path: `/bots/${botId}/settings/migrations/list?${queryString}`,
          disabled: false,
          hidden: !isUserRootOrAdmin,
        },
        {
          index: 2,
          title: getTranslation('bots.sidebar.settings.theme'),
          path: `/bots/${botId}/settings/theme/appearance?${queryString}`,
          disabled: false,
          hidden: !featureFlags.botTheme || !isUserRootOrAdmin,
        },
      ],
    },
  ]
}
